/* eslint-disable no-restricted-syntax -- Shadow color tokens; theme primitive. ADR-0050. */
import { Platform } from 'react-native';
import { colors } from './colors';

export type ShadowSize = 'sm' | 'md' | 'lg';

/**
 * Fold a shadow color + opacity into a CSS color for `boxShadow`.
 * Hex (`#000` / `#000000` / `#00A3DA`) is expanded to `rgba(...)`. A color
 * that already carries its own alpha (a `rgba(...)` string or a `$token`)
 * is passed through unchanged when opacity is 1 (the case for our
 * token-based shadows).
 */
function toBoxShadowColor(color: string, opacity: number): string {
  if (opacity >= 1) return color;
  const hex = color.replace('#', '');
  const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
  if (full.length === 6 && /^[0-9a-fA-F]{6}$/.test(full)) {
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
}

/**
 * Cross-platform drop shadow (ADR-0050 pattern). React Native Web + Tamagui
 * v2 deprecate the `shadow*` style props on web in favor of CSS `boxShadow`,
 * so emit `boxShadow` there. Native keeps the RN `shadow*` props (still the
 * supported path on iOS/Android) so native rendering is unchanged.
 *
 * Spread into a Tamagui component's props, a `StyleSheet.create` entry, or a
 * `styled()` variant. `color` accepts a hex, an `rgba(...)` string, or a
 * `$token` (tokens/`rgba` should pass `opacity: 1` and carry their own alpha).
 */
export function dropShadow(opts: {
  offsetX?: number;
  offsetY: number;
  radius: number;
  opacity: number;
  color: string;
  elevation?: number;
}) {
  const { offsetX = 0, offsetY, radius, opacity, color, elevation } = opts;
  if (Platform.OS === 'web') {
    return {
      boxShadow: `${offsetX}px ${offsetY}px ${radius}px ${toBoxShadowColor(color, opacity)}`,
    } as const;
  }
  const base = {
    shadowColor: color,
    shadowOffset: { width: offsetX, height: offsetY },
    shadowOpacity: opacity,
    shadowRadius: radius,
  } as const;
  return elevation != null ? { ...base, elevation } : base;
}

const SHADOW_PRESETS: Record<ShadowSize, { offsetY: number; radius: number; opacity: number; elevation: number; webRadius: number }> = {
  sm: { offsetY: 1, radius: 4, opacity: 0.08, elevation: 2, webRadius: 4 },
  md: { offsetY: 2, radius: 8, opacity: 0.12, elevation: 4, webRadius: 8 },
  lg: { offsetY: 4, radius: 16, opacity: 0.18, elevation: 6, webRadius: 16 },
};

/**
 * Polaroid-style shadow used by CardFrame, PolaroidCard, and the toast
 * viewport. Pass a shadow rgba string for theme-aware drops (the
 * standard pattern — `useThemeColors().polaroidShadow`). Pass a `size`
 * to use a tighter (`'sm'`) or softer (`'lg'`) drop than the default.
 */
export function getPolaroidShadowStyles(
  shadowRgba: string = colors.polaroidShadow,
  size: ShadowSize = 'md',
) {
  const preset = SHADOW_PRESETS[size];
  if (Platform.OS === 'web') {
    return { boxShadow: `0 ${preset.offsetY}px ${preset.webRadius}px ${shadowRgba}` as const };
  }
  return {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: preset.offsetY },
    shadowOpacity: preset.opacity,
    shadowRadius: preset.radius,
    elevation: preset.elevation,
  } as const;
}
